import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Label,
  Stack,
  Text,
  mergeStyles,
} from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { FormModal } from "../Modal";
import { HorizontalStack, VerticalStack } from "../../Stack";
import CustomDropdown from "../../CustomDropDown";
import Loader from "../../Loader";
import AttachmentCard from "./AttachmentCard";
import { OutlookAttachmentRow } from "./types";
import {
  Document,
  OutlookImportAttachmentPayload,
  OutlookImportVersionType,
} from "../../../models";
import { OutlookService } from "../../../services/OutlookService";
import { ApiClient } from "../../../network/ApiClient";
import { ApiEndpoints } from "../../../network/ApiEndpoints";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { getAllProjects } from "../../../modules/redux/Projects/project";
import { colorPallete } from "../../../assets/color";

const SUPPORTED_EXTENSIONS = [".pdf", ".doc", ".docx"];
const INVALID_NAME_CHARS = /[\\/:*?<>|]/;

const VERSION_TYPE_OPTIONS: {
  value: OutlookImportVersionType;
  labelKey: string;
  readOnly?: boolean;
}[] = [
  { value: "Draft", labelKey: "outlook_version_standalone" },
  { value: "Sent", labelKey: "outlook_version_sent" },
  { value: "Received", labelKey: "outlook_version_received", readOnly: true },
];

function isSupportedAttachment(name: string): boolean {
  const lower = name.toLowerCase();
  return SUPPORTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function keepOriginalExtension(name: string, originalName: string): string {
  let result = name.trim();
  if (!result) return originalName;
  const lowerOriginal = originalName.toLowerCase();
  const ext = SUPPORTED_EXTENSIONS.find((e) => lowerOriginal.endsWith(e));
  if (ext && !result.toLowerCase().endsWith(ext)) {
    result += ext;
  }
  const lastDot = result.lastIndexOf(".");
  if (lastDot !== -1 && lastDot < result.length - 1) {
    result =
      result.slice(0, lastDot + 1) + result.slice(lastDot + 1).toLowerCase();
  }
  return result;
}

interface OutlookImportModalProps {
  isModalOpen: boolean;
  hideModal: () => void;
  messageId: string;
  conversationId: string;
  subject?: string;
  fromEmail?: string;
  /** Mailbox the email lives in, as reported by the Outlook add-in. */
  mailboxEmail?: string;
}

export const OutlookImportModal: FC<OutlookImportModalProps> = ({
  isModalOpen,
  hideModal,
  messageId,
  conversationId,
  subject,
  fromEmail,
  mailboxEmail,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [rows, setRows] = useState<OutlookAttachmentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [folderNameError, setFolderNameError] = useState(false);
  const [projectId, setProjectId] = useState(0);
  const [docsData, setDocsData] = useState<Document[]>([]);
  const [versionType, setVersionType] =
    useState<OutlookImportVersionType>("Draft");
  const [includeEmailBody, setIncludeEmailBody] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Attachments are read from the signed-in user's mailbox, so an email opened
  // from a different mailbox can't be found. Only used to explain a load
  // failure: aliases can make the addresses differ for the same mailbox.
  const signedInEmail = useMemo<string | undefined>(() => {
    try {
      const email = JSON.parse(localStorage.getItem("user") ?? "null")?.email;
      // Stored data is unvalidated (legacy/malformed entries): only trust strings.
      return typeof email === "string" && email.trim()
        ? email.trim()
        : undefined;
    } catch {
      return undefined;
    }
  }, []);
  const isMailboxMismatch =
    !!mailboxEmail?.trim() &&
    !!signedInEmail &&
    mailboxEmail.trim().toLowerCase() !== signedInEmail.toLowerCase();

  const { data: projectData } = useAppSelector(
    (state: any) => state.fetchProjects
  );
  const projects: any[] = useMemo(
    () => (Array.isArray(projectData) ? projectData : []),
    [projectData]
  );

  useEffect(() => {
    if (isModalOpen) {
      dispatch(getAllProjects({ limit: 30, offset: 0 }));
    }
  }, [isModalOpen, dispatch]);

  // Load attachments and existing imports for the conversation, then join
  // them by attachmentId so already-imported files are locked.
  useEffect(() => {
    if (!isModalOpen || !messageId || !conversationId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setLoadError(false);
      try {
        const [attachments, importGroups] = await Promise.all([
          OutlookService.getMessageAttachments(messageId),
          // The imports lookup only enriches the already-imported markers
          // (isSynced on the attachments carries the flag itself), so a
          // failure here degrades to missing tooltips, not a broken modal.
          OutlookService.getImports(conversationId).catch((error) => {
            console.warn("Failed to load existing outlook imports:", error);
            return [];
          }),
        ]);
        if (cancelled) return;
        const importedById = new Map(
          importGroups
            .filter((group) => group.messageId === messageId)
            .flatMap((group) => group.items)
            .map((item) => [item.attachmentId, item])
        );
        setRows(
          attachments.map((attachment) => {
            const importInfo = importedById.get(attachment.attachmentId);
            const imported = attachment.isSynced || !!importInfo;
            const isSupported = isSupportedAttachment(attachment.name);
            return {
              attachment,
              importInfo,
              imported,
              isSupported,
              selected: !imported && isSupported,
              isNewVersion: false,
              newName: attachment.name,
              status: "idle",
            };
          })
        );
        if (attachments.length > 0) {
          setVersionType(attachments[0].suggestedVersionType ?? "Draft");
        }
      } catch (error) {
        console.error("Failed to load outlook attachments:", error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isModalOpen, messageId, conversationId]);

  // Resolve the selected project and load its documents for the
  // document-family dropdowns.
  useEffect(() => {
    const selectedProject = projects.find(
      (item: any) => item.name === folderName
    );
    if (!selectedProject?.id) {
      setProjectId(0);
      setDocsData([]);
      return;
    }
    setProjectId(selectedProject.id);
    let cancelled = false;
    (async () => {
      try {
        const response = await ApiClient.get<any>(
          ApiEndpoints.getProjectDocuments(selectedProject.id),
          {},
          {}
        );
        if (cancelled) return;
        let documents: any[] = [];
        if (Array.isArray(response?.data?.data)) {
          documents = response.data.data;
        } else if (Array.isArray(response?.data?.data?.Items)) {
          documents = response.data.data.Items;
        } else if (response?.data?.data && typeof response.data.data === "object") {
          documents =
            response.data.data.items || response.data.data.data || [];
        }
        setDocsData(
          documents.filter((doc: any) => doc && doc.rootDocumentName)
        );
      } catch (error) {
        console.error("Failed to load project documents:", error);
        if (!cancelled) setDocsData([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [folderName, projects]);

  const updateRow = useCallback(
    (attachmentId: string, patch: Partial<OutlookAttachmentRow>) => {
      setRows((prev) =>
        prev.map((row) =>
          row.attachment.attachmentId === attachmentId
            ? { ...row, ...patch }
            : row
        )
      );
    },
    []
  );

  // Sent/Received imports must create new documents (bt-core rule), so
  // switching away from Draft resets any new-version targets.
  const handleVersionTypeChange = (value: OutlookImportVersionType) => {
    setVersionType(value);
    if (value !== "Draft") {
      setRows((prev) =>
        prev.map((row) =>
          row.isNewVersion
            ? {
                ...row,
                isNewVersion: false,
                rootDocumentId: undefined,
                documentFamilyName: undefined,
              }
            : row
        )
      );
    }
  };

  const importedTooltip = useCallback(
    (row: OutlookAttachmentRow): string | undefined => {
      if (!row.importInfo) return undefined;
      const project = projects.find(
        (item: any) => item.id === row.importInfo?.projectId
      );
      const date = row.importInfo.importedAt
        ? new Date(row.importInfo.importedAt).toLocaleDateString()
        : "";
      return String(
        t("outlook_imported_to", {
          project: project?.name ?? `#${row.importInfo.projectId ?? "?"}`,
          date,
        })
      );
    },
    [projects, t]
  );

  const pendingRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          row.selected &&
          !row.imported &&
          row.isSupported &&
          row.status !== "done" &&
          row.status !== "importing"
      ),
    [rows]
  );

  const validate = (targets: OutlookAttachmentRow[]): boolean => {
    if (!projectId) {
      setFolderNameError(true);
      setValidationError(String(t("error_project")));
      return false;
    }
    let hasInvalid = false;
    setRows((prev) =>
      prev.map((row) => {
        if (!targets.some((tRow) => tRow.attachment.attachmentId === row.attachment.attachmentId)) {
          return { ...row, isInvalid: false };
        }
        const badName =
          !row.isNewVersion &&
          (!row.newName.trim() || INVALID_NAME_CHARS.test(row.newName));
        const badTarget = row.isNewVersion && !row.rootDocumentId;
        if (badName || badTarget) hasInvalid = true;
        return { ...row, isInvalid: badName || badTarget };
      })
    );
    if (hasInvalid) {
      setValidationError(String(t("outlook_invalid_rows")));
      return false;
    }
    setValidationError("");
    return true;
  };

  const runImport = async (attachmentIds?: string[]) => {
    const targets = attachmentIds
      ? rows.filter(
          (row) =>
            attachmentIds.includes(row.attachment.attachmentId) &&
            !row.imported &&
            row.status !== "done"
        )
      : pendingRows;
    if (targets.length === 0) {
      setValidationError(String(t("outlook_nothing_selected")));
      return;
    }
    if (!validate(targets)) return;

    const targetIds = new Set(targets.map((row) => row.attachment.attachmentId));
    setRows((prev) =>
      prev.map((row) =>
        targetIds.has(row.attachment.attachmentId)
          ? { ...row, status: "importing", error: undefined }
          : row
      )
    );
    setIsSubmitting(true);
    try {
      const attachments: OutlookImportAttachmentPayload[] = targets.map(
        (row) => ({
          attachmentId: row.attachment.attachmentId,
          versionType,
          ...(row.isNewVersion
            ? { rootDocumentId: row.rootDocumentId }
            : { newDocumentName: keepOriginalExtension(row.newName, row.attachment.name) }),
        })
      );
      const results = await OutlookService.createImport({
        messageId,
        conversationId,
        projectId,
        attachments,
        ...(includeEmailBody
          ? {
              emailDetails: {
                emailNotes: "",
                subject: subject ?? "",
                fromEmail,
              },
            }
          : {}),
      });
      const resultById = new Map(
        results.map((result) => [result.attachmentId, result])
      );
      setRows((prev) =>
        prev.map((row) => {
          if (!targetIds.has(row.attachment.attachmentId)) return row;
          const result = resultById.get(row.attachment.attachmentId);
          if (!result) {
            return {
              ...row,
              status: "failed",
              error: t("outlook_import_failed") as string,
            };
          }
          if (result.error) {
            return { ...row, status: "failed", error: result.error };
          }
          return { ...row, status: "done", documentId: result.documentId };
        })
      );
    } catch (error: any) {
      console.error("Outlook import failed:", error);
      const message =
        typeof error?.message === "string"
          ? error.message
          : (t("outlook_import_failed") as string);
      setRows((prev) =>
        prev.map((row) =>
          targetIds.has(row.attachment.attachmentId)
            ? { ...row, status: "failed", error: message }
            : row
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !isLoading && !isSubmitting && pendingRows.length > 0 && !!projectId;
  const allDone =
    rows.length > 0 &&
    rows.every(
      (row) => row.imported || row.status === "done" || !row.isSupported
    );

  return (
    <FormModal
      title={t("outlook_import_title")}
      TitleBackgroundColor={"linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)"}
      isModalOpen={isModalOpen}
      showModal={() => {}}
      hideModal={hideModal}
      TitleColor={colorPallete.black1}
      backgroundColor="linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)"
      width="610px"
      cancelButton
      customCancel={hideModal}
      disableScroll={true}
    >
      <div
        style={{
          background: "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)",
          padding: "10px",
        }}
      >
        <VerticalStack style={styles.container}>
          {!!subject && (
            <Text style={styles.subjectText} title={subject}>
              {subject}
            </Text>
          )}

          <Label style={styles.headingText} required>
            {t("project_name")}
          </Label>
          <CustomDropdown
            options={projects.map((item: any) => item.name)}
            title={t("project_dd")}
            data={setFolderName}
            error={setFolderNameError}
            folderName={folderName}
          />
          {folderNameError && (
            <Text style={styles.errorText}>{t("error_project")}</Text>
          )}

          <VerticalStack style={{ marginTop: "20px", gap: "8px" }}>
            <Label style={styles.headingText}>
              {t("outlook_attachments")}
            </Label>
            {isLoading ? (
              <HorizontalStack style={styles.loaderRow}>
                <Loader />
                <Text style={{ ...styles.mediumText, marginLeft: "10px" }}>
                  {t("outlook_loading_attachments")}
                </Text>
              </HorizontalStack>
            ) : loadError && isMailboxMismatch ? (
              <Text style={styles.errorText}>
                {t("outlook_mailbox_mismatch", {
                  mailbox: mailboxEmail,
                  account: signedInEmail,
                })}
              </Text>
            ) : loadError ? (
              <VerticalStack style={{ gap: "4px" }}>
                <Text style={styles.errorText}>
                  {t("outlook_attachments_error")}
                </Text>
                <Text style={styles.mediumText}>
                  {t("outlook_attachments_error_account_hint")}
                </Text>
              </VerticalStack>
            ) : rows.length === 0 ? (
              <Text style={styles.mediumText}>
                {t("outlook_no_attachments")}
              </Text>
            ) : (
              <VerticalStack
                style={{ gap: "8px", maxHeight: "260px", overflowY: "auto" }}
              >
                {rows.map((row) => (
                  <AttachmentCard
                    key={row.attachment.attachmentId}
                    row={row}
                    docsData={docsData}
                    allowVersionToggle={versionType === "Draft"}
                    isSubmitting={isSubmitting}
                    importedTooltip={importedTooltip(row)}
                    onSelectChange={(checked) =>
                      updateRow(row.attachment.attachmentId, {
                        selected: checked,
                      })
                    }
                    onNameSave={(name) =>
                      updateRow(row.attachment.attachmentId, {
                        newName: keepOriginalExtension(
                          name,
                          row.attachment.name
                        ),
                      })
                    }
                    onTargetChange={(
                      isNewVersion,
                      rootDocumentId,
                      documentFamilyName
                    ) =>
                      updateRow(row.attachment.attachmentId, {
                        isNewVersion,
                        rootDocumentId,
                        documentFamilyName,
                      })
                    }
                    onRetry={() => runImport([row.attachment.attachmentId])}
                  />
                ))}
              </VerticalStack>
            )}
          </VerticalStack>

          <VerticalStack style={{ marginTop: "20px", gap: "8px" }}>
            <Label style={styles.headingText}>{t("outlook_version_type")}</Label>
            <HorizontalStack style={{ gap: "8px" }}>
              {VERSION_TYPE_OPTIONS.map((option) => {
                const isActive = versionType === option.value;
                return (
                  <HorizontalStack
                    key={option.value}
                    onClick={() =>
                      !isSubmitting && handleVersionTypeChange(option.value)
                    }
                    style={{
                      ...styles.toggleButton,
                      border: isActive
                        ? `1px solid ${colorPallete.btBlue}`
                        : "1px solid #D0D0D0",
                    }}
                  >
                    <Stack
                      style={{
                        ...styles.toggleRadio,
                        border: isActive
                          ? "1px solid #18468F"
                          : "1px solid #707070",
                      }}
                    >
                      {isActive && <Stack style={styles.toggleRadioFill} />}
                    </Stack>
                    <Text style={styles.toggleLabel}>
                      {t(option.labelKey)}
                      {option.readOnly ? ` ${t("outlook_read_only")}` : ""}
                    </Text>
                  </HorizontalStack>
                );
              })}
            </HorizontalStack>
          </VerticalStack>

          <VerticalStack style={{ marginTop: "16px", marginBottom: "20px" }}>
            <Checkbox
              label={String(t("outlook_include_email_body"))}
              checked={includeEmailBody}
              disabled={isSubmitting}
              onChange={(_ev, checked) => setIncludeEmailBody(!!checked)}
            />
          </VerticalStack>

          {!!validationError && (
            <Text style={styles.errorText}>{validationError}</Text>
          )}
        </VerticalStack>

        <Stack className={mergeStyles(styles.horizontalLine)} />
        <HorizontalStack style={styles.bottom}>
          {allDone ? (
            <Text
              className="addButton"
              style={{
                ...styles.actionButton,
                background: colorPallete.btBlue,
                color: colorPallete.white,
                border: `1px solid ${colorPallete.btBlue}`,
              }}
              onClick={hideModal}
            >
              {t("outlook_close")}
            </Text>
          ) : (
            <>
              <Text
                className="addButton"
                style={{
                  ...styles.actionButton,
                  background: canSubmit ? colorPallete.btBlue : "#EAEAEA",
                  color: canSubmit ? colorPallete.white : "#717070",
                  border: canSubmit
                    ? `1px solid ${colorPallete.btBlue}`
                    : "1px solid #D3D3D3",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  pointerEvents: canSubmit ? "auto" : "none",
                }}
                onClick={() => canSubmit && runImport()}
              >
                {t("import")}
                {pendingRows.length > 0 ? ` (${pendingRows.length})` : ""}
              </Text>
              <Text
                className="cancelButton"
                style={styles.cancelButton}
                onClick={hideModal}
              >
                {t("cancel")}
              </Text>
            </>
          )}
        </HorizontalStack>
      </div>
    </FormModal>
  );
};

const styles = {
  container: {
    paddingLeft: "25px",
    paddingRight: "25px",
    backgroundColor: "white",
    paddingTop: "20px",
    paddingBottom: "10px",
    borderRadius: "8px 8px 0 0",
  } as React.CSSProperties,
  subjectText: {
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1F2430",
    marginBottom: "16px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as React.CSSProperties,
  headingText: {
    color: "#1F2430",
    fontFamily: "Poppins",
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "24px",
  } as React.CSSProperties,
  mediumText: {
    color: "#323232",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "16px",
  } as React.CSSProperties,
  errorText: {
    color: colorPallete.btRed,
    fontFamily: "Poppins",
    fontSize: "13px",
    marginTop: "4px",
  } as React.CSSProperties,
  loaderRow: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "6px",
    border: "1px solid #E8ECF4",
    background: "#F8F8F8",
    alignItems: "center",
  } as React.CSSProperties,
  toggleButton: {
    flex: 1,
    height: "36px",
    backgroundColor: "#F8F8F8",
    alignItems: "center" as const,
    borderRadius: "5px",
    cursor: "pointer",
    gap: "6px",
  },
  toggleRadio: {
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    alignItems: "center" as const,
    justifyContent: "center" as const,
    marginLeft: "12px",
    position: "relative" as const,
    flexShrink: 0,
  },
  toggleRadioFill: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#18468F",
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  toggleLabel: {
    fontFamily: "Poppins",
    fontSize: "13px",
    fontWeight: "400" as const,
    color: "#323232",
    lineHeight: "16px",
  },
  horizontalLine: {
    height: "1px",
    backgroundColor: "#EAEAEA",
    width: "100%",
  },
  bottom: {
    padding: "20px 25px",
    alignSelf: "flex-end",
    gap: "9px",
    justifyContent: "flex-end",
    backgroundColor: "white",
    borderRadius: "0 0 8px 8px",
  } as React.CSSProperties,
  actionButton: {
    padding: "12px 24px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px",
    cursor: "pointer",
    width: "120px",
    textAlign: "center" as const,
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "500",
  } as React.CSSProperties,
  cancelButton: {
    padding: "12px 24px",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px",
    backgroundColor: "#E8ECF4",
    color: "#323232",
    cursor: "pointer",
    width: "120px",
    textAlign: "center" as const,
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: "500",
  } as React.CSSProperties,
};
